import React, { useState, useEffect, useRef } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";
import { toLocalDateString } from "src/utils/formatUtils";
import {
  SUBMISSION_METHODS,
  getMethodLabel,
  getStatusBadge,
  formatTimestamp,
} from "./constants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { SortDescriptor } from "@progress/kendo-data-query";
import ReactDOM from "react-dom";
import {
  IScrubSession,
  IScrubSessionsResponse,
  IScrubSessionFilters,
  IScrubLogEmailResponse,
} from "src/interfaces/Reports/IScrubSession";
import ScrubSessionDetail from "./ScrubSessionDetail";

// --- Custom Cells (module scope to avoid re-creation on every render) ---

const DateCell: React.FC<GridCellProps> = (props) => {
  const { dataItem, field } = props;
  if (!dataItem || !field) return <td>-</td>;
  return <td>{formatTimestamp(dataItem[field])}</td>;
};

const StatusCell: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const badge = getStatusBadge(dataItem.statusId);
  const label = dataItem.statusDescription || "Unknown";
  return (
    <td>
      <span className={`badge ${badge}`}>{label}</span>
    </td>
  );
};

const MethodCell: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  return <td>{getMethodLabel(dataItem.receivedBy)}</td>;
};

const FileNameCell: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  if (dataItem.receivedBy?.toUpperCase() === "R") {
    return <td className="text-muted fst-italic">-- API Request --</td>;
  }
  return <td>{dataItem.originalFilename || ""}</td>;
};

const NumberCell: React.FC<GridCellProps> = (props) => {
  const { dataItem, field } = props;
  if (!dataItem || !field) return <td>-</td>;
  const value = dataItem[field];
  return <td>{value?.toLocaleString() || "0"}</td>;
};

const CampaignCell: React.FC<GridCellProps> = (props) => (
  <td>{props.dataItem?.campaignName || "N/A"}</td>
);

// --- Loading Panel ---

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: { gridRef: React.RefObject<HTMLDivElement> }) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

// --- Constants ---

const MAX_DATE_RANGE_DAYS = 31;
const MAX_DOWNLOAD_RECORDS = 50_000;

// The last 7 days, today included. A month took up to a minute to load for
// large accounts, and support rarely wants that window anyway (SON-570).
const getDefaultFilters = (): IScrubSessionFilters => {
  const dateTo = new Date();
  const dateFrom = new Date();
  dateFrom.setDate(dateFrom.getDate() - 6);
  return { dateFrom, dateTo, receivedBy: "" };
};

// --- Component ---

const ScrubLogs: React.FC = () => {
  // Grid state
  const [sessions, setSessions] = useState<IScrubSession[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState({ skip: 0, take: 25 });
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "receivedOn", dir: "desc" },
  ]);
  const gridRef = useRef<HTMLDivElement>(null);

  // Filter state (draft = form inputs, applied = used for fetching)
  // Default: last 7 days
  const [filters, setFilters] = useState<IScrubSessionFilters>(
    getDefaultFilters,
  );
  const [appliedFilters, setAppliedFilters] = useState<IScrubSessionFilters>(
    getDefaultFilters,
  );

  // Email report state (shared by both modals)
  const [reportEmail, setReportEmail] = useState("");
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [showDateRangeWarning, setShowDateRangeWarning] = useState(false);
  const [isEmailingReport, setIsEmailingReport] = useState(false);
  const [emailReportSuccess, setEmailReportSuccess] = useState(false);

  // Download too large modal
  const [showDownloadTooLarge, setShowDownloadTooLarge] = useState(false);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSession, setSelectedSession] =
    useState<IScrubSession | null>(null);

  // Track latest request so stale responses are ignored
  const requestIdRef = useRef(0);

  // --- Effects ---

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const currentSort = sort.length > 0 ? sort[0] : null;

        const params = new URLSearchParams();
        params.append("skip", String(page.skip));
        params.append("take", String(page.take));
        params.append("sortField", currentSort?.field || "receivedOn");
        params.append("sortDir", currentSort?.dir || "desc");

        if (appliedFilters.dateFrom) {
          params.append("dateFrom", toLocalDateString(appliedFilters.dateFrom));
        }
        if (appliedFilters.dateTo) {
          params.append(
            "dateTo",
            toLocalDateString(appliedFilters.dateTo) + "T23:59:59",
          );
        }
        if (appliedFilters.receivedBy) {
          params.append("receivedBy", appliedFilters.receivedBy);
        }

        const response: IScrubSessionsResponse = await callApi(
          `reports/scrub-sessions?${params.toString()}`,
          HttpMethod.GET,
        );

        if (requestId !== requestIdRef.current) return;

        setSessions(response.data || []);
        setTotal(response.total || 0);
      } catch (err: unknown) {
        if (requestId !== requestIdRef.current) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error("Error fetching scrub sessions:", err);
        setError(`Error fetching sessions: ${msg}`);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [page, sort, appliedFilters]);

  // --- Handlers ---

  const getDateRangeDays = (from: Date | null, to: Date | null): number => {
    if (!from || !to) return 0;
    return (
      Math.ceil(
        (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24),
      ) + 1
    );
  };

  // Fetch the saved report email when a modal is about to open
  const fetchReportEmail = async () => {
    setIsLoadingEmail(true);
    try {
      const res: IScrubLogEmailResponse = await callApi(
        "reports/scrub-sessions/report-email",
        HttpMethod.GET,
      );
      setReportEmail(res.email || "");
    } catch {
      // Ignore — user can still type an email manually
    } finally {
      setIsLoadingEmail(false);
    }
  };

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSearch = () => {
    const rangeDays = getDateRangeDays(filters.dateFrom, filters.dateTo);
    if (rangeDays > MAX_DATE_RANGE_DAYS) {
      setShowDateRangeWarning(true);
      fetchReportEmail();
      return;
    }
    setPage({ skip: 0, take: page.take });
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setPage({ skip: 0, take: 25 });
    setSort([{ field: "receivedOn", dir: "desc" }]);
    setAppliedFilters(defaults);
  };

  const sendEmailReport = async (f: IScrubSessionFilters) => {
    setIsEmailingReport(true);
    try {
      await callApi(
        "reports/scrub-sessions/email-report",
        HttpMethod.POST,
        {
          dateFrom: f.dateFrom ? toLocalDateString(f.dateFrom) : null,
          dateTo: f.dateTo
            ? toLocalDateString(f.dateTo) + "T23:59:59"
            : null,
          receivedBy: f.receivedBy || null,
          email: reportEmail.trim(),
        },
      );
      setEmailReportSuccess(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Error requesting email report:", err);
      setError(`Error requesting email report: ${msg}`);
      setShowDateRangeWarning(false);
      setShowDownloadTooLarge(false);
    } finally {
      setIsEmailingReport(false);
    }
  };

  const handleCloseDateRangeWarning = () => {
    setShowDateRangeWarning(false);
    setEmailReportSuccess(false);
  };

  const handleCloseDownloadTooLarge = () => {
    setShowDownloadTooLarge(false);
    setEmailReportSuccess(false);
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage({ skip: event.page.skip, take: event.page.take });
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const handleRowClick = (event: GridRowClickEvent) => {
    setSelectedSession(event.dataItem as IScrubSession);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSession(null);
  };

  // --- CSV Download ---

  const handleDownloadCsv = () => {
    if (total === 0) return;
    if (total > MAX_DOWNLOAD_RECORDS) {
      setShowDownloadTooLarge(true);
      fetchReportEmail();
      return;
    }

    // Build query params for the server-side CSV export endpoint
    const currentSort = sort.length > 0 ? sort[0] : null;
    const params = new URLSearchParams();
    params.append("sortField", currentSort?.field || "receivedOn");
    params.append("sortDir", currentSort?.dir || "desc");

    if (appliedFilters.dateFrom) {
      params.append("dateFrom", toLocalDateString(appliedFilters.dateFrom));
    }
    if (appliedFilters.dateTo) {
      params.append(
        "dateTo",
        toLocalDateString(appliedFilters.dateTo) + "T23:59:59",
      );
    }
    if (appliedFilters.receivedBy) {
      params.append("receivedBy", appliedFilters.receivedBy);
    }

    const apiUrl = runtimeConfig.apiUrl;
    window.open(
      `${apiUrl}/reports/scrub-sessions/export-csv?${params.toString()}`,
      "_blank",
    );
  };

  // --- Render ---

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Scrub Logs</h4>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={handleDownloadCsv}
          disabled={total === 0}
        >
          <i className="bi bi-download me-1"></i>Download CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-2">
              <label className="form-label mb-1">Date From</label>
              <DatePicker
                selected={filters.dateFrom}
                onChange={(date) => setFilters({ ...filters, dateFrom: date })}
                dateFormat="yyyy-MM-dd"
                isClearable
                maxDate={filters.dateTo || new Date()}
                className="form-control"
                placeholderText="Start date"
                wrapperClassName="d-block"
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label mb-1">Date To</label>
              <DatePicker
                selected={filters.dateTo}
                onChange={(date) => setFilters({ ...filters, dateTo: date })}
                dateFormat="yyyy-MM-dd"
                isClearable
                minDate={filters.dateFrom || undefined}
                maxDate={new Date()}
                className="form-control"
                placeholderText="End date"
                wrapperClassName="d-block"
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label mb-1">Method</label>
              <select
                className="form-select"
                value={filters.receivedBy}
                onChange={(e) =>
                  setFilters({ ...filters, receivedBy: e.target.value })
                }
              >
                <option value="">All</option>
                {Object.entries(SUBMISSION_METHODS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <button className="btn btn-primary me-2" onClick={handleSearch}>
                Search
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Grid */}
      <div ref={gridRef}>
        {isLoading && <LoadingPanel gridRef={gridRef} />}
        <Grid
          data={sessions}
          total={total}
          pageable={{ buttonCount: 5, pageSizes: [25, 50, 100] }}
          skip={page.skip}
          take={page.take}
          onPageChange={handlePageChange}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          resizable={true}
          onRowClick={handleRowClick}
          style={{ height: "calc(100vh - 295px)", cursor: "pointer" }}
        >
          <Column field="sessionId" title="Session ID" width={120} />
          <Column
            field="receivedOn"
            title="Date"
            width={160}
            cell={DateCell}
          />
          <Column
            field="originalFilename"
            title="File Name"
            minResizableWidth={150}
            cell={FileNameCell}
          />
          <Column
            field="numOfTotalRecords"
            title="Total"
            width={90}
            cell={NumberCell}
          />
          <Column
            field="numOfCleanRecords"
            title="Clean"
            width={85}
            cell={NumberCell}
            sortable={false}
          />
          <Column
            field="numOfBlockedRecords"
            title="Blocked"
            width={95}
            cell={NumberCell}
            sortable={false}
          />
          <Column
            field="numOfDNCRecords"
            title="DNC"
            width={80}
            cell={NumberCell}
            sortable={false}
          />
          <Column
            field="numOfWirelessRecords"
            title="Wireless"
            width={95}
            cell={NumberCell}
            sortable={false}
          />
          <Column
            field="numOfVoIPRecords"
            title="VoIP"
            width={80}
            cell={NumberCell}
            sortable={false}
          />
          <Column
            field="numOfEBRRecords"
            title="EBR"
            width={80}
            cell={NumberCell}
            sortable={false}
          />
          <Column
            field="numOfInvalidRecords"
            title="Invalid"
            width={85}
            cell={NumberCell}
            sortable={false}
          />
          <Column
            field="statusId"
            title="Status"
            width={140}
            cell={StatusCell}
            sortable={false}
          />
          <Column
            field="receivedBy"
            title="Method"
            width={90}
            cell={MethodCell}
            sortable={false}
          />
          <Column
            field="receivedByData1"
            title="IP Address"
            width={130}
            sortable={false}
          />
          <Column
            field="campaignName"
            title="Campaign"
            minResizableWidth={120}
            sortable={false}
            cell={CampaignCell}
          />
        </Grid>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedSession && (
        <ScrubSessionDetail
          session={selectedSession}
          onClose={handleCloseDetailModal}
        />
      )}

      {/* Date Range Warning Modal */}
      {showDateRangeWarning && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="date-range-warning-title"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="date-range-warning-title">Date Range Too Large</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleCloseDateRangeWarning}
                ></button>
              </div>
              <div className="modal-body">
                {emailReportSuccess ? (
                  <div className="alert alert-success mb-0">
                    <i className="bi bi-check-circle me-2"></i>
                    Your request has been submitted. You will receive an email
                    with the report shortly.
                    <div className="mt-2 small">
                      <strong>Date From:</strong>{" "}
                      {filters.dateFrom
                        ? toLocalDateString(filters.dateFrom)
                        : "N/A"}
                      <br />
                      <strong>Date To:</strong>{" "}
                      {filters.dateTo
                        ? toLocalDateString(filters.dateTo)
                        : "N/A"}
                      <br />
                      <strong>Method:</strong>{" "}
                      {filters.receivedBy
                        ? getMethodLabel(filters.receivedBy)
                        : "All"}
                    </div>
                  </div>
                ) : (
                  <>
                    <p>
                      The selected date range exceeds {MAX_DATE_RANGE_DAYS} days.
                      Due to the volume of records, this data cannot be displayed
                      in the browser.
                    </p>
                    <p>
                      Please narrow your date range to {MAX_DATE_RANGE_DAYS} days
                      or fewer, or enter your email and click{" "}
                      <strong>Email Report</strong> to have the results emailed
                      to you.
                    </p>
                    <div className="mb-2">
                      <label className="form-label mb-1">
                        Send report to <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${reportEmail && !isValidEmail(reportEmail) ? "is-invalid" : ""}`}
                        placeholder="email@example.com"
                        value={reportEmail}
                        onChange={(e) => setReportEmail(e.target.value)}
                        disabled={isLoadingEmail}
                        required
                      />
                      {reportEmail && !isValidEmail(reportEmail) && (
                        <div className="invalid-feedback">
                          Please enter a valid email address.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {!emailReportSuccess && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => sendEmailReport(filters)}
                    disabled={isEmailingReport || !isValidEmail(reportEmail)}
                  >
                    {isEmailingReport ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-envelope me-1"></i>Email Report
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseDateRangeWarning}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Download Too Large Modal */}
      {showDownloadTooLarge && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="download-too-large-title"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="download-too-large-title">Data Download Too Large</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleCloseDownloadTooLarge}
                ></button>
              </div>
              <div className="modal-body">
                {emailReportSuccess ? (
                  <div className="alert alert-success mb-0">
                    <i className="bi bi-check-circle me-2"></i>
                    Your request has been submitted. You will receive an email
                    with the report shortly.
                    <div className="mt-2 small">
                      <strong>Date From:</strong>{" "}
                      {appliedFilters.dateFrom
                        ? toLocalDateString(appliedFilters.dateFrom)
                        : "N/A"}
                      <br />
                      <strong>Date To:</strong>{" "}
                      {appliedFilters.dateTo
                        ? toLocalDateString(appliedFilters.dateTo)
                        : "N/A"}
                      <br />
                      <strong>Method:</strong>{" "}
                      {appliedFilters.receivedBy
                        ? getMethodLabel(appliedFilters.receivedBy)
                        : "All"}
                    </div>
                  </div>
                ) : (
                  <>
                    <p>
                      The download exceeds {MAX_DOWNLOAD_RECORDS.toLocaleString()} records ({total.toLocaleString()} total).
                      This report is too large to download directly.
                    </p>
                    <p>
                      Enter your email and click <strong>Email Report</strong> to
                      have the report generated in the background and emailed to
                      you once processing is complete.
                    </p>
                    <div className="mb-2">
                      <label className="form-label mb-1">
                        Send report to <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${reportEmail && !isValidEmail(reportEmail) ? "is-invalid" : ""}`}
                        placeholder="email@example.com"
                        value={reportEmail}
                        onChange={(e) => setReportEmail(e.target.value)}
                        disabled={isLoadingEmail}
                        required
                      />
                      {reportEmail && !isValidEmail(reportEmail) && (
                        <div className="invalid-feedback">
                          Please enter a valid email address.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {!emailReportSuccess && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => sendEmailReport(appliedFilters)}
                    disabled={isEmailingReport || !isValidEmail(reportEmail)}
                  >
                    {isEmailingReport ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-envelope me-1"></i>Email Report
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseDownloadTooLarge}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrubLogs;
